// Live control channel — device WebSocket client (docs/04 §Live channel).
//
// An ENHANCEMENT layer over manifest polling, which stays the source of truth.
// When a device token exists (RUNNING) the player opens a WebSocket to the API's
// live hub for real-time presence + push commands (snapshot / reload /
// refresh_manifest). Everything here is wrapped so a WS failure — a closed
// socket, a parse error, a thrown handler, or no WebSocket at all — NEVER touches
// playback or the poll loop. If the socket can't stay up the player behaves
// exactly as it does today, just without the low-latency extras.
//
// All impurities are injected (the socket factory, clock, RNG, timers, command
// side effects) so the connect/hello/throttle/backoff/dispatch state machine is
// unit-testable with no real WebSocket, no DOM, and no `window.location`.

/** Minimal socket surface we use — the global `WebSocket` satisfies it. */
export interface LiveSocket {
  send(data: string): void;
  close(): void;
  onopen: (() => void) | null;
  onclose: (() => void) | null;
  onerror: (() => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
}

/** Opens a socket to `url` negotiating `protocol` as the WS subprotocol. */
export type LiveSocketFactory = (url: string, protocol: string) => LiveSocket;

/** The item currently on screen, reported in `hello`/`state`. Null when idle. */
export interface NowPlaying {
  media_id: string;
  name?: string;
}

export interface LiveDeps {
  /** wss://…/v1/player/ws (no token — it rides the subprotocol). */
  url: string;
  /** Device token → the `seencast-device.<token>` subprotocol. */
  token: string;
  playerVersion: string;
  /** Snapshot of what's on screen right now (main zone). */
  nowPlaying: () => NowPlaying | null;
  /** Reuse the Sprint-8 snapshot capture+upload path (same 1/60s debounce). */
  onSnapshot: () => void;
  /** Trigger an immediate manifest poll (no visual interruption). */
  onRefreshManifest: () => void;
  /**
   * Fired when an OPEN socket closes (a real disconnect, not a failed connect
   * attempt). The player uses this to restore the normal (un-stretched) poll
   * cadence and poll once immediately — see app.ts traffic diet (T3).
   */
  onDisconnect?: () => void;
  /** Perform the page reload (default `location.reload`); called after a delay. */
  reload?: () => void;
  connect?: LiveSocketFactory;
  /** [0,1) source for backoff jitter; injectable for deterministic tests. */
  random?: () => number;
  setTimer?: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearTimer?: (t: ReturnType<typeof setTimeout>) => void;
  log?: (code: string, message?: string) => void;
}

/** Reconnect backoff: doubles from 1s, capped at 60s (docs/04 §Live channel). */
const BACKOFF_BASE_MS = 1000;
const BACKOFF_CAP_MS = 60_000;
const BACKOFF_JITTER = 0.3; // ±30%
/** Let a sent frame settle before navigating away on a `reload` command. */
const RELOAD_DELAY_MS = 500;

/** Un-jittered backoff for attempt N (0-based): min(1s·2^N, 60s). */
export function backoffBaseMs(attempt: number): number {
  return Math.min(BACKOFF_BASE_MS * 2 ** attempt, BACKOFF_CAP_MS);
}

/**
 * Backoff delay for attempt N with ±30% jitter (so a fleet reconnecting after a
 * hub blip spreads out instead of thundering). `rand` in [0,1) → factor in
 * [0.7, 1.3). Always ≥ 0.7·base and ≤ 1.3·base.
 */
export function jitteredBackoffMs(attempt: number, rand: number): number {
  const base = backoffBaseMs(attempt);
  const factor = 1 + (rand * 2 - 1) * BACKOFF_JITTER;
  return Math.round(base * factor);
}

type InboundCommand = 'snapshot' | 'reload' | 'refresh_manifest';

export class LiveConnection {
  private ws: LiveSocket | null = null;
  /** True only between the socket's open and close — send `state` only while open. */
  private isOpen = false;
  private stopped = true;
  /** Failed-connection counter; drives backoff, reset to 0 on a successful open. */
  private attempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reloadTimer: ReturnType<typeof setTimeout> | null = null;
  /** JSON of the last now_playing transmitted (hello or state) — throttles `state`. */
  private lastStateSent: string | null = null;

  private readonly connect: LiveSocketFactory;
  private readonly random: () => number;
  private readonly setTimer: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  private readonly clearTimer: (t: ReturnType<typeof setTimeout>) => void;
  private readonly reload: () => void;
  private readonly log: (code: string, message?: string) => void;

  constructor(private deps: LiveDeps) {
    this.connect =
      deps.connect ?? ((url, protocol) => new WebSocket(url, protocol) as unknown as LiveSocket);
    this.random = deps.random ?? Math.random;
    this.setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = deps.clearTimer ?? ((t) => clearTimeout(t));
    this.reload = deps.reload ?? (() => location.reload());
    this.log = deps.log ?? (() => {});
  }

  /** True only while the socket is open — drives the WS-connected poll stretch. */
  isConnected(): boolean {
    return this.isOpen;
  }

  /** Begin connecting (idempotent). Safe to call once on entering RUNNING. */
  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    this.openSocket();
  }

  /** Close the socket and cancel any reconnect. No further sockets are opened. */
  stop(): void {
    this.stopped = true;
    this.clearReconnect();
    if (this.reloadTimer) {
      this.clearTimer(this.reloadTimer);
      this.reloadTimer = null;
    }
    this.closeSocket();
    this.lastStateSent = null;
  }

  /**
   * Report an item change. Sends `{type:'state'}` only when now_playing actually
   * changed since the last frame we transmitted (hello counts) — an unchanged
   * item is skipped, so a fast crossfade loop doesn't spam the hub. A no-op when
   * the socket isn't open (the next `hello` will carry the current state).
   */
  notifyStateChanged(): void {
    if (!this.isOpen) return;
    const np = this.safeNowPlaying();
    const serialized = JSON.stringify(np);
    if (serialized === this.lastStateSent) return; // throttle: unchanged
    if (this.trySend({ type: 'state', now_playing: np })) {
      this.lastStateSent = serialized;
    }
  }

  private openSocket(): void {
    if (this.stopped) return;
    this.clearReconnect();
    let ws: LiveSocket;
    try {
      ws = this.connect(this.deps.url, `seencast-device.${this.deps.token}`);
    } catch (e) {
      // No WebSocket / constructor threw → treat as a failed attempt and back off.
      this.log('live_connect_failed', String(e));
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.onopen = () => this.onOpen();
    ws.onclose = () => this.onClosed();
    ws.onerror = () => this.onError();
    ws.onmessage = (ev) => this.onMessage(ev.data);
  }

  private onOpen(): void {
    this.isOpen = true;
    this.attempt = 0; // reset backoff on a successful connection
    const np = this.safeNowPlaying();
    this.lastStateSent = JSON.stringify(np);
    this.trySend({ type: 'hello', player_version: this.deps.playerVersion, now_playing: np });
  }

  private onError(): void {
    // Errors are always followed by a close on real sockets; log for visibility.
    this.log('live_ws_error');
  }

  private onClosed(): void {
    const wasOpen = this.isOpen;
    this.ws = null;
    this.isOpen = false;
    this.lastStateSent = null;
    if (this.stopped) return;
    // Only a socket that was actually OPEN counts as a disconnect worth reacting
    // to (a failed connect attempt closes without ever opening).
    if (wasOpen) {
      try {
        this.deps.onDisconnect?.();
      } catch (e) {
        this.log('live_disconnect_cb_failed', String(e));
      }
    }
    this.scheduleReconnect();
  }

  private onMessage(data: unknown): void {
    let msg: unknown;
    try {
      msg = typeof data === 'string' ? JSON.parse(data) : data;
    } catch {
      this.log('live_bad_message');
      return;
    }
    if (!msg || typeof msg !== 'object') return;
    const type = (msg as { type?: unknown }).type;
    if (type === 'ping') {
      this.trySend({ type: 'pong' });
      return;
    }
    if (type === 'command') {
      const command = (msg as { command?: unknown }).command;
      if (command === 'snapshot' || command === 'reload' || command === 'refresh_manifest') {
        this.dispatchCommand(command);
      }
    }
  }

  private dispatchCommand(command: InboundCommand): void {
    try {
      if (command === 'snapshot') {
        this.deps.onSnapshot();
      } else if (command === 'refresh_manifest') {
        this.deps.onRefreshManifest();
      } else {
        // reload: give any pending frame a moment to flush, then reload.
        if (this.reloadTimer) return; // already scheduled
        this.reloadTimer = this.setTimer(() => {
          this.reloadTimer = null;
          this.reload();
        }, RELOAD_DELAY_MS);
      }
    } catch (e) {
      this.log('live_command_failed', String(e));
    }
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.reconnectTimer) return;
    const delay = jitteredBackoffMs(this.attempt, this.random());
    this.attempt++;
    this.reconnectTimer = this.setTimer(() => {
      this.reconnectTimer = null;
      this.openSocket();
    }, delay);
  }

  private clearReconnect(): void {
    if (this.reconnectTimer) {
      this.clearTimer(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private closeSocket(): void {
    this.isOpen = false;
    if (!this.ws) return;
    const ws = this.ws;
    this.ws = null;
    ws.onopen = ws.onclose = ws.onerror = ws.onmessage = null;
    try {
      ws.close();
    } catch {
      /* already closing/closed */
    }
  }

  private safeNowPlaying(): NowPlaying | null {
    try {
      return this.deps.nowPlaying();
    } catch {
      return null;
    }
  }

  /** Serialize + send; swallow any error so a dead socket never propagates. */
  private trySend(payload: unknown): boolean {
    if (!this.ws) return false;
    try {
      this.ws.send(JSON.stringify(payload));
      return true;
    } catch (e) {
      this.log('live_send_failed', String(e));
      return false;
    }
  }
}
